import type {
  DataStructureCommon,
  DiscoveryFilterMinMax,
  DiscoveryFilterProfileStatus,
  DiscoveryFilterProfiles,
  DiscoveryFilterRange
} from 'types';

export const FOLLOWERS_STEP_SELECTOR = [
  {
    min: 0,
    max: 2,
    step: '1k'
  },
  {
    min: 2,
    max: 50,
    step: '2k'
  },
  {
    min: 50,
    max: 75,
    step: '30k'
  },
  {
    min: 75,
    max: 100,
    step: '750k'
  }
];

export const VIEWS_STEP_SELECTOR = [
  {
    min: 0,
    max: 50,
    step: '2k'
  },
  {
    min: 50,
    max: 75,
    step: '16k'
  },
  {
    min: 75,
    max: 100,
    step: '220k'
  }
];

export const getSelectedValues = (items: Array<DataStructureCommon>): Array<string> => (items || []).filter(item => item.selected).map(item => String(item.value));

export const getProfileStatus = (profiles: DiscoveryFilterProfiles, type?: 'creators' | 'content'): DiscoveryFilterProfileStatus => {
  const getFilterStatus = (profile: DataStructureCommon): boolean => (profile && profile.selected) || false;

  const statuses = {} as { verified: boolean, unverified: boolean};

  profiles.forEach(profile => {
    statuses[profile.identifier] = getFilterStatus(profile);
  });

  // COLLAB-6416
  if (type === 'creators' && !statuses.verified && !statuses.unverified) {
    statuses.verified = true;
    statuses.unverified = true;
  }

  return statuses;
};

const convertStepToNumber = (step: string): number => {
  if (step.indexOf('k') >= 0) {
    return Number(step.replace('k', '000'));
  }

  if (step.indexOf('m') >= 0) {
    return Number(step.replace('m', '000000'));
  }

  return Number(step);
};

const calculatePreviousValues = (toIndex: number, stepSelector: Array<DiscoveryFilterRange>): number => {
  let index = 0;
  let value = 0;

  while (index < toIndex) {
    value += (stepSelector[index].max - stepSelector[index].min) * convertStepToNumber(stepSelector[index].step);
    index++;
  }

  return value;
};

export const transformSteps = (range: DiscoveryFilterMinMax, stepSelector: Array<DiscoveryFilterRange>): Partial<DiscoveryFilterMinMax> => {
  const { min, max } = range;

  // search min and then max to see in which range fit
  const minRangeStepIndex = stepSelector.findIndex(step => min >= step.min && min <= step.max);
  const maxRangeStepIndex = stepSelector.findIndex(step => max >= step.min && max <= step.max);

  if (minRangeStepIndex === -1 || maxRangeStepIndex === -1) {
    return {};
  }

  const minRangeStep = stepSelector[minRangeStepIndex];
  const maxRangeStep = stepSelector[maxRangeStepIndex];

  const prevMinValue = calculatePreviousValues(minRangeStepIndex, stepSelector);
  const prevMaxValue = calculatePreviousValues(maxRangeStepIndex, stepSelector);

  return {
    min: prevMinValue + minRangeStep.min + (convertStepToNumber(minRangeStep.step) * (min - minRangeStep.min)),
    max: prevMaxValue + maxRangeStep.min + (convertStepToNumber(maxRangeStep.step) * (max - maxRangeStep.min))
  };
};
